<script>
import { mapState } from 'vuex';
import findIndex from 'lodash/findIndex';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ScheduledServiceTimeModel from '@models/ScheduledServiceTime';
import JobModel from '@models/Job';
import ScheduledServiceTimeForm from '@views/jobs/scheduledServiceTimes/scheduled-service-time-form';
import ScheduledServiceTimeModal from '@views/jobs/scheduledServiceTimes/edit-scheduled-service-time';
import utility from '@mixins/utility';
import datePickerUtility from '@mixins/datePickerUtility';
import LocationModel from '@models/Location';
import flatPickr from 'vue-flatpickr-component';
import EntityMultiselect from '@components/EntityMultiselect';
import CarTypeModel from '@models/CarType';
import CarForm from '@views/cars/form';
import ReportModel from '@models/Report';

import map from 'lodash/map';

export default {
  components: {
    FontAwesomeIcon,
    ScheduledServiceTimeForm,
    ScheduledServiceTimeModal,
    flatPickr,
    EntityMultiselect,
    CarForm,
  },
  mixins: [utility, datePickerUtility],
  props: {
    collapsed: {
      default: false,
      type: Boolean,
      required: false,
    },
    // eslint-disable-next-line
    job: {
      required: true,
      default: null,
    },
    scheduledServiceTimes: {
      default: () => [],
      type: Array,
    },
  },
  data() {
    return {
      locationModel: LocationModel,
      reportModel: ReportModel,
      selectedScheduledServiceTime: null,
      scheduledServiceTimesLocal: this.scheduledServiceTimes,
      duplicateSST: null,
      showDuplicateSSTModal: false,
      num_of_duplicates: 1,
      showAddReportModal: false,
      selectedSSTForReport: null,
      selectedReportId: null,
      reportMultiselectSelect: [],
      reportMultiselectInclude: ['vip', 'company', 'user'],
      reportMultiselectWhere: [
        {
          name: 'search',
        },
        {
          name: 'lock_status',
          value: true
        },
      ],
      reportMultiselectURLParams: {
        all_drafts: true,
      },
      fields: [
        { key: 'selected', sortable: false, label: this.$t('table_labels.select') },
        {
          key: 'id',
          label: this.$t('table_labels.id'),
          sortable: true,
        },
        {
          key: 'car',
          label: this.$t('table_labels.car'),
          sortable: false,
        },
        {
          key: 'car_type',
          label: this.$t('table_labels.car_type'),
          sortable: false,
        },
        {
          key: 'location_from',
          label: this.$t('fields.location_from'),
          sortable: false,
        },
        {
          key: 'location_to',
          label: this.$t('fields.location_to'),
          sortable: false,
        },
        {
          key: 'start_timestamp',
          label: this.$t('fields.service_start'),
          sortable: false,
          formatter: this.timeFormatter,
        },
        {
          key: 'end_timestamp',
          label: this.$t('fields.service_end'),
          sortable: false,
          formatter: this.timeFormatter,
        },
        {
          key: 'driver_or_service_provider',
          label: this.$t('table_labels.driver_or_service_provider'),
          sortable: false,
        },
        {
          key: 'report',
          label: this.$t('fields.report'),
          sortable: false,
        },
        {
          key: 'actions',
          label: this.$t('table_labels.actions'),
          sortable: false,
        },
      ],
      newRemark: null,
      selectAll: false,
      selectedItems: [],
      selectionOptionSelected: null,
      selectionOptions: [
        { text: this.$t('fields.select_option'), value: null },
        { text: this.$t('fields.change_service_times'), value: 0 },
        { text: this.$t('fields.change_locations'), value: 1 },
        { text: this.$t('fields.change_car'), value: 2 },
        { text: this.$t('fields.select_to_add_report'), value: 3 },
        { text: this.$t('fields.select_to_delete'), value: 4 },
      ],
      serviceStartTime: null,
      serviceEndTime: null,
      locationFrom: null,
      locationTo: null,
      locationFromId: null,
      locationToId: null,
      useExistingCar: false,
      scheduledServiceTime: {
        car_type_id: null,
        car_id: null,
        car: {
          car_type: {
            name: '',
          },
          car_type_id: '',
        },
      },
      defaultScheduledServiceTime: {
        car_type_id: null,
        car_id: null,
        car: {
          car_type: {
            name: '',
          },
          car_type_id: '',
        },
      },
      carTypes: [],
      reportId: null,
      report: null,
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateConfigTime,
    }),
  },
  mounted() {
    CarTypeModel.select(['id', 'name'])
      .orderBy(`name`)
      .page(1)
      .limit(100)
      .get()
      .then((response) => {
        this.carTypes = response.data;
        this.$wait.end('car types loading');
      })
      .catch(() => {
        this.$wait.end('car types loading');
      });
  },
  methods: {
    updateRemark(scheduledServiceTime) {
      const job = new JobModel({ id: this.job.id });
      const data = scheduledServiceTime;
      // clear data - send only sst info
      data.job = null;
      new ScheduledServiceTimeModel(data)
        .for(job)
        .save()
        .then((response) => {
          this.update(response.scheduled_service_time);
        });
    },
    async duplicate() {
      if (this.duplicateSST && this.num_of_duplicates) {
        this.$wait.start('scheduledServiceTime processing');
        this.$http
          .post(`admin/scheduled-service-times/${this.duplicateSST}/duplicate`, {
            num_of_duplicates: this.num_of_duplicates,
          })
          .then((response) => {
            this.$wait.end('scheduledServiceTime processing');
            this.$emit('duplicated', response.data.scheduled_service_times);
            this.closeDuplicateSSTModal();
          })
          .catch(() => {
            this.$wait.end('scheduledServiceTime processing');
            this.closeDuplicateSSTModal();
          });
      } else {
        this.$toastead.error('SST not selected or number of duplicates is < 1');
      }
    },
    submit(scheduledServiceTime) {
      this.$emit('submitted', scheduledServiceTime);
    },
    update(scheduledServiceTime) {
      this.selectedScheduledServiceTime = null;
      const key = findIndex(this.scheduledServiceTimesLocal, { id: scheduledServiceTime.id });
      this.$emit('updated', scheduledServiceTime, key);
    },
    async deleteScheduledServiceTime(item) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.scheduled_service_time') }))) {
        this.$wait.start(`scheduled_service_time${item.id}`);
        const key = findIndex(this.scheduledServiceTimesLocal, { id: item.id });
        const job = new JobModel({ id: this.job.id });
        const scheduledServiceTime = new ScheduledServiceTimeModel({ id: item.id });
        await scheduledServiceTime.for(job).delete();
        this.$emit('deleted', item.id, key);
        this.$wait.end(`scheduled_service_time${item.id}`);
      }
    },
    timeFormatter(value) {
      return value ? DateTime.fromSQL(value).toFormat('dd.LL.yyyy HH:mm') : null;
    },
    selectAllSSTs() {
      if (this.selectAll) {
        this.selectedItems = map(this.scheduledServiceTimesLocal, (item) => {
          return { id: item.id };
        });
      } else {
        this.selectedItems = [];
      }
    },
    clearSelected() {
      this.selectedItems = [];
      this.selectAll = false;
      this.selectionOptionSelected = null;
    },
    clearUpdateData() {
      this.serviceStartTime = null;
      this.serviceEndTime = null;
      this.locationFrom = null;
      this.locationTo = null;
      this.locationFromId = null;
      this.locationToId = null;
      this.useExistingCar = false;
      this.reportId = null;
      this.report = null;
      this.scheduledServiceTime = Object.assign({}, this.defaultScheduledServiceTime);
    },
    deleteMultipleSSTs() {
      const ids = map(this.selectedItems, (item) => {
        return item.id;
      });

      const keys = map(ids, (id) => {
        return findIndex(this.scheduledServiceTimesLocal, { id: id });
      });

      this.$wait.start('scheduled-service-times bulk-delete loading');
      this.$http
        .put('admin/scheduled-service-times/bulk-delete', {
          ids: ids,
        })
        .then(() => {
          this.$wait.end('scheduled-service-times bulk-delete loading');
          this.clearSelected();
          this.$emit('deleted-multiple', keys);
        })
        .catch(() => this.$wait.end('scheduled-service-times bulk-delete loading'));
    },
    updateMultipleSSTs() {
      const ids = map(this.selectedItems, (item) => {
        return item.id;
      });

      const keys = map(ids, (id) => {
        return findIndex(this.scheduledServiceTimesLocal, { id: id });
      });
      this.$wait.start('scheduled-service-times bulk-update loading');
      this.$http
        .put('admin/scheduled-service-times/bulk-update', {
          ids: ids,
          job_id: this.job.id,
          location_from_id: this.locationFromId,
          location_to_id: this.locationToId,
          start_timestamp: this.serviceStartTime,
          end_timestamp: this.serviceEndTime,
          car_id: this.scheduledServiceTime.car_id,
          car_type_id: this.scheduledServiceTime.car_type_id,
          report_id: this.reportId,
        })
        .then((response) => {
          this.$wait.end('scheduled-service-times bulk-update loading');
          this.clearSelected();
          this.clearUpdateData();
          this.$emit('updated-multiple', response.data.scheduled_service_times, keys);
        })
        .catch(() => this.$wait.end('scheduled-service-times bulk-update loading'));
    },
    openDuplicateSSTModal(id) {
      this.duplicateSST = id;
      this.showDuplicateSSTModal = true;
    },
    closeDuplicateSSTModal() {
      this.duplicateSST = null;
      this.showDuplicateSSTModal = false;
      this.num_of_duplicates = 1;
    },
    openAddReportModal(item) {
      this.selectedSSTForReport = item;
      this.selectedReportId = item.report_id;
      this.showAddReportModal = true;
    },
    closeAddReportModal() {
      this.selectedSSTForReport = null;
      this.selectedReportId = null;
      this.showAddReportModal = false;
    },
    async submitAddReport() {
      this.$wait.start('sst-add-report-processing');
      const job = new JobModel({ id: this.job.id });
      
      // Clone the whole scheduled service time object and update report_id
      const scheduledServiceTime = new ScheduledServiceTimeModel({
        ...this.selectedSSTForReport,
        report_id: this.selectedReportId,
      });
      
      scheduledServiceTime
        .for(job)
        .save()
        .then((response) => {
          this.$wait.end('sst-add-report-processing');
          this.update(response.scheduled_service_time);
          this.closeAddReportModal();
        })
        .catch(() => {
          this.$wait.end('sst-add-report-processing');
        });
    },
  },
};
</script>

<template>
  <div>
    <b-collapse id="service-times-collapse" v-model="collapsed" class="mt-2 mb-2">
      <ScheduledServiceTimeForm ref="scheduledServiceTimeForm" :car-types-prop="carTypes" :job="job" @submitted="submit" />
    </b-collapse>
    <p v-if="$route.name != 'jobs.edit'">{{ $t('titles.service_times') }}</p>
    <b-row v-else class="mb-4">
      <b-col sm="12" md="4" lg="4">
        <b-form-group>
          <template v-slot:label class="col-form-legend col-12">{{ $t('fields.selection_options') }}:</template>
          <b-form-select
            v-model="selectionOptionSelected"
            :options="selectionOptions"
            :disabled="!isJobEditable(job)"
            @change="clearUpdateData"
          ></b-form-select>
          <b-form-text> </b-form-text>
        </b-form-group>
      </b-col>
      <template v-if="selectionOptionSelected === 3">
        <b-col sm="7">
          <b-form-group :label="`${$t('fields.report')}`">
            <EntityMultiselect
              key="report-input"
              v-model="reportId"
              v-validate="''"
              data-vv-name="report_id"
              :data-vv-as="$t('fields.report')"
              :initial-value="report"
              :state="errors.has('report_id', 'scheduled-service-time-form')"
              :placeholder="$t('placeholders.report')"
              sort="id"
              :model="reportModel"
              :select="reportMultiselectSelect"
              :where="reportMultiselectWhere"
              :include="reportMultiselectInclude"
              :params="reportMultiselectURLParams"
            >
            </EntityMultiselect>
          </b-form-group>
        </b-col>
      </template>
      <template v-if="selectionOptionSelected === 2">
        <b-col sm="6">
          <b-form-checkbox v-model="useExistingCar" name="check-button" switch class="mb-2">
            {{ $t('fields.use_existing_car') }}
          </b-form-checkbox>
          <b-form-group v-if="!useExistingCar">
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.car_type') }}:
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="carTypePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="carTypePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.car_type') }}
                </b-popover>
              </span>
            </template>
            <b-form-select
              v-model="scheduledServiceTime.car_type_id"
              data-vv-value-path="localValue"
              data-vv-name="car_type_id"
              :data-vv-as="$t('fields.car_type')"
              :state="errors.has('car_type_id', 'scheduled-service-time-form') ? false : null"
              aria-describedby="input-feedback-car_type_id"
            >
              <option :value="null">{{ $t('placeholders.car_type') }}</option>
              <option v-for="carType in carTypes" :key="`carTypeSelect${carType.id}`" :value="carType.id">
                {{ carType.name }}
              </option>
            </b-form-select>
          </b-form-group>
          <CarForm v-else v-model="scheduledServiceTime" :loaded-id="scheduledServiceTime.car_id" :is-car-use-form="true">&nbsp;</CarForm>
        </b-col>
      </template>
      <template v-if="selectionOptionSelected === 1">
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.location_from') }}: </template>
            <EntityMultiselect
              v-model="locationFromId"
              v-validate="'required'"
              data-vv-name="location_from"
              :data-vv-as="$t('fields.location_from')"
              :initial-value="locationFrom"
              :state="errors.has('locationFrom', 'scheduled-service-time-form')"
              :placeholder="$t('placeholders.location')"
              :model="locationModel"
            >
            </EntityMultiselect>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.location_to') }}: </template>
            <EntityMultiselect
              v-model="locationToId"
              v-validate="'required'"
              data-vv-name="location_to"
              :data-vv-as="$t('fields.location_to')"
              :initial-value="locationTo"
              :state="errors.has('location_to', 'scheduled-service-time-form')"
              :placeholder="$t('placeholders.location')"
              :model="locationModel"
            >
            </EntityMultiselect>
          </b-form-group>
        </b-col>
      </template>
      <template v-if="selectionOptionSelected === 0">
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.service_start') }}: </template>
            <flat-pickr
              ref="serviceStart"
              v-model="serviceStartTime"
              v-validate="'required'"
              data-vv-name="start_timestamp"
              :data-vv-as="$t('fields.service_start')"
              aria-describedby="input-feedback-start_timestamp"
              :config="dateConfig"
              class="form-control"
              :class="{ 'is-invalid': errors.has('start_timestamp', 'scheduled-service-time-form') }"
              :placeholder="$t('placeholders.service_start')"
            >
            </flat-pickr>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.service_end') }}: </template>
            <flat-pickr
              ref="serviceEnd"
              v-model="serviceEndTime"
              v-validate="'required'"
              data-vv-name="end_timestamp"
              :data-vv-as="$t('fields.service_end')"
              aria-describedby="input-feedback-end_timestamp"
              :config="dateConfig"
              class="form-control"
              :class="{ 'is-invalid': errors.has('end_timestamp', 'scheduled-service-time-form') }"
              :placeholder="$t('placeholders.service_end')"
            >
            </flat-pickr>
          </b-form-group>
        </b-col>
      </template>
      <template v-if="selectionOptionSelected === 4">
        <b-form-group>
          <template v-slot:label class="col-form-legend col-12">&nbsp;</template>
          <b-button type="submit" variant="outline-primary" :disabled="selectedItems.length === 0" @click.stop="deleteMultipleSSTs">
            {{ $t('actions.delete') }}
          </b-button>
        </b-form-group>
      </template>
      <template v-if="selectionOptionSelected !== null && selectionOptionSelected !== 4">
        <b-col sm="1">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">&nbsp;</template>
            <b-button type="submit" variant="outline-primary" :disabled="selectedItems.length === 0" @click.stop="updateMultipleSSTs">
              {{ $t('actions.update') }}
            </b-button>
          </b-form-group>
        </b-col>
      </template>
    </b-row>
    <small v-if="scheduledServiceTimesLocal.length" class="table-responsive" style="overflow: inherit">
      <b-table ref="asyncTable" stacked="lg" striped hover :items="scheduledServiceTimesLocal" :fields="fields" small table-class="no-min-width">
        <template #head(selected)="data">
          <span>{{ data.label }}</span>
          <span><b-form-checkbox id="checkboxSelectAll" v-model="selectAll" :disabled="!isJobEditable(job)" @change="selectAllSSTs"> </b-form-checkbox></span>
        </template>
        <template v-slot:cell(selected)="data">
          <b-form-checkbox
            :id="'checkbox' + data.index"
            v-model="selectedItems"
            :value="{
              id: data.item.id,
            }"
            :disabled="!isJobEditable(job)"
          >
          </b-form-checkbox>
        </template>
        <template v-slot:cell(car)="row">
          <div v-if="row.item.car"> # {{ row.item.car.id }} - {{ row.item.car.license_plate }}</div>
        </template>
        <template v-slot:cell(location_from)="row">
          <div>{{ row.item.location_from.name }}</div>
        </template>
        <template v-slot:cell(location_to)="row">
          <div>{{ row.item.location_to.name }}</div>
        </template>
        <template v-slot:cell(car_type)="row">
          <div v-if="row.item.car">{{ row.item.car.car_type.name }}</div>
          <div v-else-if="row.item.car_type">{{ row.item.car_type.name }}</div>
        </template>
        <template v-slot:cell(driver_or_service_provider)="row">
          <div v-if="row.item.driver_id">{{ row.item.driver.name }}</div>
          <div v-if="row.item.service_provider_id">{{ row.item.service_provider.name }}</div>
        </template>
        <template v-slot:cell(report)="row">
          <router-link
            v-if="row.item.report && row.item.report.id != null && row.item.report.deleted_at == null"
            :to="{ name: 'reports.edit', params: { id: row.item.report.id } }"
            >#{{ row.item.report.id }}</router-link
          >
          <span v-else-if="row.item.report.id != null && row.item.report" class="text-danger">#{{ row.item.report.id }}</span>
          <span v-else></span>
        </template>
        <template v-slot:cell(actions)="row">
          <b-btn class="mr-2" size="sm" :variant="row.detailsShowing ? 'secondary' : 'outline-secondary'" @click.stop="row.toggleDetails">
            <FontAwesomeIcon :icon="['fas', 'sticky-note']" />
          </b-btn>
          <b-dropdown variant="outline-primary" dropleft no-caret size="sm">
            <template #button-content>
              <FontAwesomeIcon :icon="['fas', 'ellipsis-h']" />
            </template>

            <b-dropdown-item variant="primary" :disabled="$wait.is(`scheduledServiceTime${row.item.id}`)" @click="selectedScheduledServiceTime = row.item.id">
              <FontAwesomeIcon :icon="['fas', isJobEditable(job) ? 'edit' : 'eye']" />
              <small>&nbsp;{{ $t('actions.edit') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="info" :disabled="$wait.is(`scheduledServiceTime${row.item.id}`)" @click="openDuplicateSSTModal(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'copy']" />
              <small>&nbsp;{{ $t('actions.duplicate') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="success" :disabled="$wait.is(`scheduledServiceTime${row.item.id}`)" @click="openAddReportModal(row.item)">
              <FontAwesomeIcon :icon="['fas', 'file-alt']" />
              <small>&nbsp;{{ $t('fields.add_report') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="danger" :disabled="$wait.is(`scheduledServiceTime${row.item.id}`)" @click="deleteScheduledServiceTime(row.item)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
              <small>&nbsp;{{ $t('actions.delete') }}</small>
            </b-dropdown-item>
          </b-dropdown>
        </template>
        <template v-slot:row-details="row">
          <div class="px-2">
            <label>{{ $t('fields.remark') }}</label>
            <b-form-group>
              <b-form-textarea
                id="remark"
                v-model="row.item.remark"
                v-validate="'max:65000'"
                class="mb-2"
                data-vv-value-path="localValue"
                data-vv-name="remark"
                :rows="3"
                aria-describedby="input-feedback-remark"
              >
              </b-form-textarea>
              <b-btn variant="primary" @click="updateRemark(row.item)">{{ $t('actions.update') }}</b-btn>
            </b-form-group>
          </div>
        </template>
      </b-table>
    </small>

    <ScheduledServiceTimeModal
      v-model="selectedScheduledServiceTime"
      :loaded-id="selectedScheduledServiceTime"
      :job="job"
      @updated="update"
      @hidden="selectedScheduledServiceTime = null"
    />
    <b-modal v-if="showDuplicateSSTModal" v-model="showDuplicateSSTModal" size="md" hide-footer :title="$t('messages.how_many_duplicates')" static>
      <b-form data-vv-scope="sst-duplicate-form">
        <b-form-group>
          <b-form-input
            v-model="num_of_duplicates"
            v-validate="'required|numeric|min:1|max:20'"
            type="number"
            step="1"
            min="1"
            max="20"
            data-vv-value-path="localValue"
            data-vv-name="num_of_duplicates"
            :state="errors.has('num_of_duplicates', 'sst-duplicate-form') ? false : null"
            aria-describedby="input-feedback-num_of_duplicates"
          ></b-form-input>
          <b-form-invalid-feedback id="input-feedback-num_of_duplicates">
            <template v-show="errors.has('num_of_duplicates', 'sst-duplicate-form')">{{ errors.first('num_of_duplicates', 'sst-duplicate-form') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>
        <div class="pull-right text-right">
          <b-button variant="outline-danger" class="mr-2" @click="closeDuplicateSSTModal">{{ this.$t('actions.cancel_de') }}</b-button>
          <b-button :disabled="errors.any()" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')" @click="duplicate">
            {{ $t('actions.ok') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal v-if="showAddReportModal" v-model="showAddReportModal" size="md" hide-footer :title="$t('fields.add_report')" static>
      <b-form data-vv-scope="sst-add-report-form">
        <b-form-group :label="`${$t('fields.report')}:`">
          <EntityMultiselect
            v-model="selectedReportId"
            v-validate="'required'"
            data-vv-name="report_id"
            :data-vv-as="$t('fields.report')"
            :initial-value="selectedSSTForReport ? selectedSSTForReport.report : null"
            :state="errors.has('report_id', 'sst-add-report-form') ? false : null"
            :placeholder="$t('placeholders.report')"
            sort="id"
            :model="reportModel"
            :select="reportMultiselectSelect"
            :where="reportMultiselectWhere"
            :include="reportMultiselectInclude"
            :params="reportMultiselectURLParams"
            reference="SST"
          >
          </EntityMultiselect>
          <b-form-invalid-feedback :state="errors.has('report_id', 'sst-add-report-form') ? false : null">
            <template v-show="errors.has('report_id', 'sst-add-report-form')">{{ errors.first('report_id', 'sst-add-report-form') }}</template>
          </b-form-invalid-feedback>
        </b-form-group>
        <div class="pull-right text-right">
          <b-button variant="outline-danger" class="mr-2" @click="closeAddReportModal">{{ $t('actions.cancel_de') }}</b-button>
          <b-button :disabled="$wait.is('sst-add-report-processing') || !selectedReportId" variant="outline-primary" @click="submitAddReport">
            {{ $t('actions.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>
<style lang="scss" module>
.statusContainer {
  padding-left: 1rem;
  margin: 0;
}

.statusLoadingIcon {
  margin: 0 auto;
}
</style>
